/**
 * Contesto operativo persistente (UL + registro in sidebar), per utente Cognito.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "@/lib/AuthProvider";

export interface OperatoreContextValue {
  cf: string;
  numIscr: string;
  denominazione: string;
  numIscrSito: string;
  sitoLabel: string;
  registroId: string;
  setCf: (v: string) => void;
  setOperatore: (numIscr: string, denominazione: string) => void;
  setSito: (numIscrSito: string, label?: string) => void;
  setRegistroId: (id: string) => void;
}

const defaultValue: OperatoreContextValue = {
  cf: "",
  numIscr: "",
  denominazione: "",
  numIscrSito: "",
  sitoLabel: "",
  registroId: "",
  setCf: () => {},
  setOperatore: () => {},
  setSito: () => {},
  setRegistroId: () => {},
};

const OperatoreContext = createContext<OperatoreContextValue>(defaultValue);

function storageKeyFor(email: string): string {
  const e = email.trim().toLowerCase();
  return e ? `rentri.operatoreContext.${e}` : "rentri.operatoreContext";
}

function loadStored(key: string): Partial<OperatoreContextValue> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<OperatoreContextValue>) : {};
  } catch {
    return {};
  }
}

export function OperatoreProvider({ children }: { children: ReactNode }) {
  const { email } = useAuth();
  const key = storageKeyFor(email);

  const [cf, setCfState] = useState("");
  const [numIscr, setNumIscr] = useState("");
  const [denominazione, setDenominazione] = useState("");
  const [numIscrSito, setNumIscrSito] = useState("");
  const [sitoLabel, setSitoLabel] = useState("");
  const [registroId, setRegistroIdState] = useState("");

  useEffect(() => {
    const stored = loadStored(key);
    setCfState(stored.cf ?? "");
    setNumIscr(stored.numIscr ?? "");
    setDenominazione(stored.denominazione ?? "");
    setNumIscrSito(stored.numIscrSito ?? "");
    setSitoLabel(stored.sitoLabel ?? "");
    setRegistroIdState(stored.registroId ?? "");
  }, [key]);

  useEffect(() => {
    if (!email) return;
    localStorage.setItem(
      key,
      JSON.stringify({ cf, numIscr, denominazione, numIscrSito, sitoLabel, registroId }),
    );
  }, [key, email, cf, numIscr, denominazione, numIscrSito, sitoLabel, registroId]);

  const setCf = useCallback((v: string) => setCfState(v), []);
  const setOperatore = useCallback((iscr: string, denom: string) => {
    setNumIscr(iscr);
    setDenominazione(denom);
  }, []);
  const setSito = useCallback((sito: string, label = "") => {
    setNumIscrSito(sito);
    setSitoLabel(label);
  }, []);
  const setRegistroId = useCallback((id: string) => setRegistroIdState(id), []);

  const value = useMemo(
    () => ({
      cf,
      numIscr,
      denominazione,
      numIscrSito,
      sitoLabel,
      registroId,
      setCf,
      setOperatore,
      setSito,
      setRegistroId,
    }),
    [cf, numIscr, denominazione, numIscrSito, sitoLabel, registroId, setCf, setOperatore, setSito, setRegistroId],
  );

  return (
    <OperatoreContext.Provider value={value}>{children}</OperatoreContext.Provider>
  );
}

export function useOperatoreContext() {
  return useContext(OperatoreContext);
}
